/**
 * RefinementFlow - Multi-round image refinement interface.
 *
 * Displays 4 generated image variations in a 2x2 grid.
 * User selects one, then can accept or refine (up to 4 refinement rounds).
 *
 * Supports voice input for refinement feedback using the same
 * hold-to-speak pattern as the conversation input (issue #177).
 *
 * Design: DENiZEN
 * - Soft shadows and rounded corners
 * - Muted palette with mid-green accents
 * - High contrast selection states
 * - WCAG 2.1 AA compliant
 */

import { useState, useEffect, useCallback, useRef, memo } from 'react'
import type { ImageVariation } from '@/services/api'
import type { GenerationStatus } from '@/components/image/GenerationProgress'
import { useSpeechToText, type TranscribeFunction } from '@/hooks/useSpeechToText'
import { ImageLightbox } from './ImageLightbox'
import { ImageGridItem } from './ImageGridItem'
import { RoundTimeline } from './RoundTimeline'
import type { RoundState } from '@/hooks/useImageGeneration'

/**
 * Suggested feedback prompts for refinement
 */
const SUGGESTED_PROMPTS = [
  'Add more green spaces',
  'More people interacting',
  'Warmer colours',
  'Community gathering areas',
  'Accessibility features',
]

export interface RefinementFlowProps {
  /**
   * Current image variations to display (4 images)
   */
  variations: ImageVariation[]
  /**
   * Index of currently selected variation (0-3), null if none selected
   */
  selectedIndex: number | null
  /**
   * Callback when user selects a variation
   */
  onSelectVariation: (index: number) => void
  /**
   * Callback when user accepts the selected image
   */
  onAccept: () => void
  /**
   * Callback when user requests refinement with feedback
   */
  onRefine: (feedback: string) => void
  /**
   * Current round number (1-5)
   */
  currentRound: number
  /**
   * Maximum number of rounds (typically 5)
   */
  maxRounds: number
  /**
   * Whether refinement is allowed (round < maxRounds and image selected)
   */
  canRefine: boolean
  /**
   * Current generation status
   */
  status: GenerationStatus
  /**
   * Whether generating/refining is in progress
   */
  isLoading: boolean
  /**
   * Error message to display
   */
  error?: string | null
  /**
   * Elapsed time in milliseconds (for progress display)
   */
  elapsedMs?: number
  /**
   * Callback to retry generation after failure
   */
  onRetry?: () => void
  /**
   * Callback to go back to previous round
   */
  onGoBack?: () => void
  /**
   * Whether user can navigate back to a previous round
   */
  canGoBack?: boolean
  /**
   * All generation rounds for timeline display
   */
  rounds?: RoundState[]
  /**
   * Navigate to a specific round by index (truncates forward history)
   */
  onNavigateToRound?: (roundIndex: number) => void
  /**
   * Function to transcribe audio via backend API.
   * When provided, enables voice input on the refinement feedback form.
   * Reuses the same transcription endpoint as conversation input.
   * @security Addresses issue #75 - keeps API credentials on backend
   */
  transcribeAudio?: TranscribeFunction
}

export const RefinementFlow = memo(function RefinementFlow({
  variations,
  selectedIndex,
  onSelectVariation,
  onAccept,
  onRefine,
  currentRound,
  maxRounds,
  canRefine,
  status,
  isLoading,
  error,
  elapsedMs = 0,
  onRetry,
  onGoBack,
  canGoBack = false,
  rounds,
  onNavigateToRound,
  transcribeAudio,
}: RefinementFlowProps) {
  const [showFeedbackForm, setShowFeedbackForm] = useState(false)
  const [feedback, setFeedback] = useState('')
  const [selectedSuggestion, setSelectedSuggestion] = useState<string | null>(null)
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const [previewImage, setPreviewImage] = useState<ImageVariation | null>(null)
  const [showBackConfirm, setShowBackConfirm] = useState(false)

  // Focus management for back-confirmation dialog (WCAG 2.1.2, 2.4.3)
  const backButtonRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)
  const hasOpenedDialog = useRef(false)

  useEffect(() => {
    if (showBackConfirm) {
      hasOpenedDialog.current = true
      // Move focus to first interactive element inside the dialog
      const firstButton = dialogRef.current?.querySelector('button')
      firstButton?.focus()
    } else if (hasOpenedDialog.current) {
      // Return focus to the trigger button when dialog closes (not on initial mount)
      backButtonRef.current?.focus()
    }
  }, [showBackConfirm])

  // --- Voice input for refinement feedback (issue #177) ---
  const FEEDBACK_MAX_LENGTH = 2000
  const MIN_RECORDING_DURATION_MS = 500
  const AUTO_CONTINUE_THRESHOLD_MS = 5000
  const pressStartTimeRef = useRef<number | null>(null)
  const autoContinueTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isAutoContinue, setIsAutoContinue] = useState(false)
  const [isHolding, setIsHolding] = useState(false)
  const [shortRecordingMessage, setShortRecordingMessage] = useState<string | null>(null)
  const [charLimitWarning, setCharLimitWarning] = useState<string | null>(null)
  const [voiceError, setVoiceError] = useState<string | null>(null)

  const {
    isListening,
    transcript: speechTranscript,
    error: speechError,
    isSupported: isSpeechSupported,
    isTranscribing,
    startListening,
    stopListening,
    cancelListening,
    resetTranscript,
  } = useSpeechToText({
    transcribe: transcribeAudio,
    onTranscriptionComplete: (text) => {
      // Check character limit after transcription
      const newLength = feedback.length + (feedback && !feedback.endsWith(' ') ? 1 : 0) + text.length
      if (newLength > FEEDBACK_MAX_LENGTH) {
        setCharLimitWarning(`Transcription would exceed ${FEEDBACK_MAX_LENGTH} character limit. Text has been truncated.`)
        setTimeout(() => setCharLimitWarning(null), 4000)
      }
    },
  })

  const hasSpeechSupport = isSpeechSupported && !!transcribeAudio
  const isSpeechDisabled = isLoading || isTranscribing

  // Track latest isListening value for use in setTimeout callbacks (avoids stale closure)
  const isListeningRef = useRef(isListening)
  useEffect(() => {
    isListeningRef.current = isListening
  }, [isListening])

  // Append transcribed text to feedback field
  useEffect(() => {
    if (speechTranscript) {
      setFeedback((prev) => {
        const separator = prev && !prev.endsWith(' ') ? ' ' : ''
        const combined = prev + separator + speechTranscript
        // Truncate to character limit
        return combined.slice(0, FEEDBACK_MAX_LENGTH)
      })
      // Voice input modifies the textarea content, so deselect any active chip
      setSelectedSuggestion(null)
      resetTranscript()
    }
  }, [speechTranscript, resetTranscript])

  // Clean up auto-continue timeout on unmount
  useEffect(() => {
    return () => {
      if (autoContinueTimeoutRef.current) {
        clearTimeout(autoContinueTimeoutRef.current)
      }
    }
  }, [])

  // Helper to clear auto-continue timeout
  const clearAutoContinueTimeout = useCallback((): void => {
    if (autoContinueTimeoutRef.current) {
      clearTimeout(autoContinueTimeoutRef.current)
      autoContinueTimeoutRef.current = null
    }
  }, [])

  // Helper to reset all hold-to-speak state
  const resetHoldState = useCallback((): void => {
    pressStartTimeRef.current = null
    setIsAutoContinue(false)
    setIsHolding(false)
    clearAutoContinueTimeout()
  }, [clearAutoContinueTimeout])

  // Reset hold state if speech recognition fails during hold
  useEffect(() => {
    if (speechError && pressStartTimeRef.current !== null) {
      resetHoldState()
    }
  }, [speechError, resetHoldState])

  // Safe wrapper for stopListening that catches any synchronous exceptions (#144)
  // The hook handles async errors internally via speechError state, but this guards
  // against edge cases where the function itself might throw before returning
  const safeStopListening = useCallback((context: string): void => {
    try {
      // Use void to explicitly mark the promise as intentionally not awaited
      // The hook handles async errors internally via speechError state
      void stopListening()
    } catch (err: unknown) {
      console.error('[RefinementFlow] stopListening failed:', {
        error: err instanceof Error ? err.message : String(err),
        errorType: err instanceof Error ? err.name : typeof err,
        operation: 'stopListening',
        context,
        wasListening: isListeningRef.current,
      })
    }
  }, [stopListening])

  // Hold-to-speak handlers (same pattern as TextInput)
  const handleMicMouseDown = useCallback(() => {
    if (isSpeechDisabled) return
    pressStartTimeRef.current = Date.now()
    setIsAutoContinue(false)
    setIsHolding(true)
    clearAutoContinueTimeout()

    startListening().catch((err: unknown) => {
      // Surface error in UI instead of only logging to console
      const message = err instanceof Error ? err.message : String(err)
      console.error('[RefinementFlow] Failed to start listening:', {
        error: message,
        errorType: err instanceof Error ? err.name : typeof err,
        operation: 'startListening',
        wasHolding: true,
        wasAutoContinue: false,
      })
      setVoiceError(message)
      setTimeout(() => setVoiceError(null), 5000)
    })

    autoContinueTimeoutRef.current = setTimeout(() => {
      if (pressStartTimeRef.current !== null && isListeningRef.current) {
        setIsAutoContinue(true)
      }
    }, AUTO_CONTINUE_THRESHOLD_MS)
  }, [isSpeechDisabled, startListening, clearAutoContinueTimeout])

  const handleMicMouseUp = useCallback(() => {
    if (isSpeechDisabled) return
    clearAutoContinueTimeout()

    const holdDuration = pressStartTimeRef.current
      ? Date.now() - pressStartTimeRef.current
      : 0

    pressStartTimeRef.current = null
    setIsHolding(false)

    if (!isAutoContinue && isListening) {
      if (holdDuration < MIN_RECORDING_DURATION_MS) {
        cancelListening()
        setShortRecordingMessage('Hold longer while speaking')
        setTimeout(() => setShortRecordingMessage(null), 2500)
        return
      }
      safeStopListening('mouse-up')
    }
  }, [isSpeechDisabled, isAutoContinue, isListening, clearAutoContinueTimeout, cancelListening, safeStopListening])

  const handleMicClick = useCallback(() => {
    if (isAutoContinue && isListening) {
      safeStopListening('tap-to-stop')
      setIsAutoContinue(false)
    }
  }, [isAutoContinue, isListening, safeStopListening])

  const handleMicMouseLeave = useCallback(() => {
    if (pressStartTimeRef.current !== null && !isAutoContinue && isListening) {
      const holdDuration = Date.now() - pressStartTimeRef.current
      if (holdDuration < MIN_RECORDING_DURATION_MS) {
        cancelListening()
        setShortRecordingMessage('Hold longer while speaking')
        setTimeout(() => setShortRecordingMessage(null), 2500)
      } else {
        safeStopListening('mouse-leave')
      }
      clearAutoContinueTimeout()
    }
    pressStartTimeRef.current = null
    setIsHolding(false)
  }, [isAutoContinue, isListening, safeStopListening, clearAutoContinueTimeout, cancelListening])

  // Handle Escape key to close confirmation dialog (WCAG 2.1 AA requirement)
  useEffect(() => {
    if (!showBackConfirm) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowBackConfirm(false)
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [showBackConfirm])

  const isGenerating = status === 'generating' || status === 'queued'
  const refinementsRemaining = maxRounds - currentRound
  const selectedVariation = selectedIndex !== null ? variations[selectedIndex] : null

  // Stable callbacks for memoised ImageGridItem - prevent re-renders on hover
  const handleMouseEnter = useCallback((index: number) => {
    setHoveredIndex(index)
  }, [])

  const handleMouseLeave = useCallback(() => {
    setHoveredIndex(null)
  }, [])

  const handleSelectVariation = useCallback(
    (index: number) => {
      if (!isLoading) {
        onSelectVariation(index)
      }
    },
    [isLoading, onSelectVariation]
  )

  const handlePreviewVariation = useCallback((variation: ImageVariation) => {
    setPreviewImage(variation)
  }, [])

  const handleRefine = () => {
    if (feedback.trim() && !isLoading) {
      onRefine(feedback.trim())
      setFeedback('')
      setSelectedSuggestion(null)
      setShowFeedbackForm(false)
    }
  }

  const handleSuggestedPrompt = (prompt: string) => {
    setFeedback(prompt)
    setSelectedSuggestion(prompt)
  }

  const formatElapsedTime = (ms: number): string => {
    const seconds = Math.floor(ms / 1000)
    return `${seconds}s`
  }

  return (
    <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 py-6">
      {/* Header */}
      <header className="mb-4 text-center">
        <h2 className="text-3xl font-display font-bold text-dark-green mb-2">
          {isGenerating ? 'Creating Your Visions' : 'Choose Your Vision'}
        </h2>
        <p className="text-base font-body text-dark-green/80">
          {isGenerating
            ? 'AI is generating 4 unique images based on your conversation...'
            : "Select the image that best represents your community's future"}
        </p>
        {isGenerating && (
          <p className="text-dark-green/60 font-body text-sm mt-2">
            Generating... {formatElapsedTime(elapsedMs)}
          </p>
        )}
      </header>

      <hr className="border-dark-green/15 mb-6" />

      {/* Error display with retry */}
      {error && (
        <div
          className="mb-6 p-4 bg-error border border-dark-green/15 shadow-denizen-sm rounded-2xl"
          role="alert"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 bg-white border border-dark-green/15 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-error" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <p className="text-white text-sm font-body flex-1 break-words">{error}</p>
          </div>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="w-full py-2 px-4 font-display font-bold text-sm tracking-wider border border-dark-green/15 bg-white text-dark-green shadow-denizen-sm hover:-translate-y-1 active:shadow-none transition-all duration-150 rounded-2xl"
            >
              Try Again
            </button>
          )}
        </div>
      )}

      {/* Round history — visible when more than one round exists */}
      {rounds && rounds.length > 1 && onNavigateToRound && (
        <RoundTimeline
          rounds={rounds}
          currentRoundIndex={rounds.length - 1}
          onNavigateToRound={onNavigateToRound}
          disabled={isLoading}
        />
      )}

      {/* 2x2 Grid */}
      <div
        className="grid grid-cols-2 gap-4 mb-6"
        role={isGenerating ? undefined : 'radiogroup'}
        aria-label={isGenerating ? 'Images being generated' : 'Image selection grid'}
      >
        {isGenerating ? (
          // Skeleton placeholders during generation
          Array.from({ length: 4 }).map((_, index) => (
            <div
              key={`skeleton-${index}`}
              className="relative aspect-square overflow-hidden border border-dark-green/15 bg-stone rounded-2xl shadow-denizen-sm"
            >
              {/* Animated shimmer effect */}
              <div
                className="absolute inset-0 animate-pulse"
                style={{
                  background:
                    'linear-gradient(90deg, #fff8eb 0%, #f0ead8 50%, #fff8eb 100%)',
                  backgroundSize: '200% 100%',
                  animation: 'shimmer 1.5s ease-in-out infinite',
                }}
              />

              {/* Center icon */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 border border-dark-green/15 rounded-full flex items-center justify-center bg-white shadow-denizen-sm">
                  <svg
                    className="w-8 h-8 text-muted animate-spin"
                    style={{ animationDuration: '2s' }}
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="3"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                </div>
              </div>

              {/* Number badge */}
              <div className="absolute top-3 left-3 w-10 h-10 rounded-full border border-dark-green/15 flex items-center justify-center font-display font-bold text-lg bg-white shadow-denizen-sm">
                {index + 1}
              </div>

              {/* Status label */}
              <div className="absolute bottom-3 left-3 right-3 px-3 py-2 border border-dark-green/15 text-center rounded-2xl bg-white">
                <span className="text-xs font-display font-bold text-muted">
                  Generating...
                </span>
              </div>
            </div>
          ))
        ) : (
          // Actual images when available - using memoised ImageGridItem
          <>
            {variations.map((variation, index) => (
              <ImageGridItem
                key={variation.image_id}
                variation={variation}
                index={index}
                isSelected={selectedIndex === index}
                isHovered={hoveredIndex === index}
                isDisabled={isLoading || showFeedbackForm}
                onSelect={handleSelectVariation}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onPreview={handlePreviewVariation}
              />
            ))}

            {/* Empty slots if fewer than 4 images */}
            {Array.from({ length: Math.max(0, 4 - variations.length) }).map((_, i) => (
              <div
                key={`empty-${i}`}
                className="aspect-square border border-dark-green/15 border-dashed bg-stone rounded-2xl flex items-center justify-center"
              >
                <div className="text-muted font-body text-sm">Not available</div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Action Section */}
      {!isGenerating && (
        <div className="animate-slide-up border-t border-dark-green/15 pt-6 mt-2 relative">
          {/* Round label */}
          <div className="absolute -top-3 right-0 text-xs font-display font-bold text-dark-green/60 bg-stone px-2">
            Round {currentRound}
          </div>

          {!showFeedbackForm ? (
            <>
              {/* Selection prompt or action buttons */}
              {selectedVariation === null ? (
                <div className="text-center py-4">
                  <p className="text-muted font-body">
                    Click an image above to select it
                  </p>
                </div>
              ) : (
                <>
                  {/* Info text */}
                  <p className="text-sm font-body text-muted mb-4">
                    {canRefine ? (
                      <>
                        You can refine this image{' '}
                        <span className="font-display font-bold text-dark-green">
                          {refinementsRemaining} more{' '}
                          {refinementsRemaining === 1 ? 'time' : 'times'}
                        </span>
                        .
                      </>
                    ) : (
                      <>
                        This is your final round. You can accept your selected
                        image.
                      </>
                    )}
                  </p>

                  {/* Action buttons */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    {/* Back button - Secondary */}
                    {canGoBack && (
                      <button
                        ref={backButtonRef}
                        type="button"
                        onClick={() => setShowBackConfirm(true)}
                        disabled={isLoading}
                        className={`
                          py-4 px-6 font-display font-bold text-sm tracking-wider
                          border border-dark-green/15 bg-white text-dark-green
                          shadow-denizen-sm rounded-2xl
                          transition-all duration-150
                          focus:outline-none focus-visible:ring-4 focus-visible:ring-mid-green focus-visible:ring-offset-2
                          ${
                            isLoading
                              ? 'opacity-50 cursor-not-allowed'
                              : 'hover:-translate-y-1 active:shadow-none'
                          }
                        `}
                      >
                        <span className="flex items-center justify-center gap-2">
                          <svg
                            className="w-5 h-5"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            aria-hidden="true"
                          >
                            <path
                              fillRule="evenodd"
                              d="M11.03 3.97a.75.75 0 0 1 0 1.06l-6.22 6.22H21a.75.75 0 0 1 0 1.5H4.81l6.22 6.22a.75.75 0 1 1-1.06 1.06l-7.5-7.5a.75.75 0 0 1 0-1.06l7.5-7.5a.75.75 0 0 1 1.06 0Z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Back
                        </span>
                      </button>
                    )}

                    {/* Accept button - mid-green */}
                    <button
                      type="button"
                      onClick={onAccept}
                      disabled={isLoading}
                      className={`
                        flex-1 py-4 px-6 font-display font-bold text-sm tracking-wider
                        border border-dark-green/15 rounded-2xl
                        shadow-denizen-sm
                        transition-all duration-150
                        focus:outline-none focus-visible:ring-4 focus-visible:ring-mid-green focus-visible:ring-offset-2
                        ${
                          isLoading
                            ? 'bg-muted text-white cursor-not-allowed'
                            : 'bg-dark-green text-white hover:-translate-y-1 active:shadow-none'
                        }
                      `}
                    >
                      <span className="flex items-center justify-center gap-2">
                        <svg
                          className="w-5 h-5"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            fillRule="evenodd"
                            d="M19.916 4.626a.75.75 0 0 1 .208 1.04l-9 13.5a.75.75 0 0 1-1.154.114l-6-6a.75.75 0 0 1 1.06-1.06l5.353 5.353 8.493-12.74a.75.75 0 0 1 1.04-.207Z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Accept This Vision
                      </span>
                    </button>

                    {/* Refine button - White */}
                    {canRefine && (
                      <button
                        type="button"
                        onClick={() => setShowFeedbackForm(true)}
                        disabled={isLoading}
                        className={`
                          flex-1 py-4 px-6 font-display font-bold text-sm tracking-wider
                          border border-dark-green/15 bg-white text-dark-green rounded-2xl
                          shadow-denizen-sm
                          transition-all duration-150
                          focus:outline-none focus-visible:ring-4 focus-visible:ring-mid-green focus-visible:ring-offset-2
                          ${
                            isLoading
                              ? 'opacity-50 cursor-not-allowed'
                              : 'hover:-translate-y-1 active:shadow-none'
                          }
                        `}
                      >
                        <span className="flex items-center justify-center gap-2">
                          <svg
                            className="w-5 h-5"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            aria-hidden="true"
                          >
                            <path d="M21.731 2.269a2.625 2.625 0 0 0-3.712 0l-1.157 1.157 3.712 3.712 1.157-1.157a2.625 2.625 0 0 0 0-3.712ZM19.513 8.199l-3.712-3.712-8.4 8.4a5.25 5.25 0 0 0-1.32 2.214l-.8 2.685a.75.75 0 0 0 .933.933l2.685-.8a5.25 5.25 0 0 0 2.214-1.32l8.4-8.4Z" />
                            <path d="M5.25 5.25a3 3 0 0 0-3 3v10.5a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3V13.5a.75.75 0 0 0-1.5 0v5.25a1.5 1.5 0 0 1-1.5 1.5H5.25a1.5 1.5 0 0 1-1.5-1.5V8.25a1.5 1.5 0 0 1 1.5-1.5h5.25a.75.75 0 0 0 0-1.5H5.25Z" />
                          </svg>
                          Refine ({refinementsRemaining} left)
                        </span>
                      </button>
                    )}
                  </div>
                </>
              )}
            </>
          ) : (
            /* Feedback form */
            <div className="space-y-4">
              {/* Feedback textarea with voice input */}
              <div>
                <label
                  htmlFor="refinementFeedback"
                  className="block text-xs font-display font-bold text-dark-green tracking-wider mb-2"
                >
                  What would you like to change?
                </label>
                <div className="relative">
                  <div className="flex items-end gap-2">
                    <textarea
                      id="refinementFeedback"
                      value={feedback}
                      onChange={(e) => {
                        if (e.target.value.length <= FEEDBACK_MAX_LENGTH) {
                          setFeedback(e.target.value)
                          setSelectedSuggestion(null)
                        }
                      }}
                      placeholder="Describe the changes you'd like to see..."
                      rows={3}
                      maxLength={FEEDBACK_MAX_LENGTH}
                      className="flex-1 px-4 py-3 font-body text-dark-green bg-white border border-dark-green/15 shadow-denizen-sm rounded-2xl placeholder:text-muted focus:outline-none focus:shadow-denizen transition-shadow duration-150 resize-none"
                      aria-describedby="feedback-hint feedback-char-count"
                    />

                    {/* Microphone button - same pattern as TextInput (issue #177) */}
                    {hasSpeechSupport && (
                      <div className="relative group flex-shrink-0 mb-1">
                        <button
                          type="button"
                          onMouseDown={handleMicMouseDown}
                          onMouseUp={handleMicMouseUp}
                          onMouseLeave={handleMicMouseLeave}
                          onClick={handleMicClick}
                          onTouchStart={(e) => { e.preventDefault(); handleMicMouseDown() }}
                          onTouchEnd={(e) => { e.preventDefault(); handleMicMouseUp() }}
                          onTouchCancel={handleMicMouseLeave}
                          disabled={isSpeechDisabled}
                          className="w-9 h-9 border border-dark-green/15 rounded-full flex items-center justify-center transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-mid-green focus-visible:ring-offset-1"
                          style={{
                            backgroundColor: isSpeechDisabled ? '#F5F5F5' : (isHolding || isListening) ? 'var(--color-teal, #2D8B8B)' : '#FFFFFF',
                            boxShadow: isSpeechDisabled ? 'none' : (isHolding || isListening) ? 'var(--shadow-denizen-sm)' : 'var(--shadow-denizen-sm)',
                            transform: (isHolding || isListening) && !isSpeechDisabled ? 'translateY(1px)' : 'none',
                            cursor: isSpeechDisabled ? 'not-allowed' : 'pointer',
                            animation: isListening ? 'pulse-recording 1s ease-in-out infinite' : 'none',
                          }}
                          aria-label={
                            isListening
                              ? (isAutoContinue ? 'Tap to stop recording' : 'Release to stop recording')
                              : 'Hold to speak'
                          }
                          aria-pressed={isListening}
                          aria-disabled={isSpeechDisabled}
                          data-testid="refinement-mic-button"
                        >
                          <svg
                            className="w-4 h-4"
                            style={{
                              color: isSpeechDisabled ? '#9CA3AF' : (isHolding || isListening) ? '#FFFFFF' : 'var(--color-dark-green, #1A3A2A)',
                            }}
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
                            <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
                          </svg>
                        </button>
                        {/* Tooltip */}
                        <span
                          className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-white border border-dark-green/15 shadow-denizen-sm font-body text-xs text-dark-green whitespace-nowrap opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 pointer-events-none transition-opacity rounded-2xl"
                        >
                          {isListening
                            ? (isAutoContinue ? 'Tap to stop' : 'Release to stop')
                            : 'Hold to speak'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Voice status indicators */}
                  {(speechError || voiceError) && (
                    <div
                      className="mt-2 px-3 py-2 text-xs font-body border border-dark-green/15 rounded-2xl bg-error/20 text-dark-green"
                      role="alert"
                      data-testid="refinement-speech-error"
                    >
                      {speechError || voiceError}
                    </div>
                  )}
                  {(isListening || isTranscribing) && !speechError && (
                    <div
                      className="mt-2 px-3 py-2 text-xs font-body border border-dark-green/15 rounded-2xl bg-light-green text-dark-green"
                      role="status"
                      aria-live="polite"
                      data-testid="refinement-recording-status"
                    >
                      {isTranscribing ? 'Transcribing...' : 'Recording...'}
                    </div>
                  )}
                  {shortRecordingMessage && !isListening && (
                    <div
                      className="mt-2 px-3 py-2 text-xs font-body border border-dark-green/15 rounded-2xl bg-stone text-dark-green animate-fade-in"
                      role="status"
                      aria-live="polite"
                    >
                      {shortRecordingMessage}
                    </div>
                  )}
                  {charLimitWarning && (
                    <div
                      className="mt-2 px-3 py-2 text-xs font-body border border-dark-green/15 rounded-2xl bg-stone text-dark-green animate-fade-in"
                      role="status"
                      aria-live="polite"
                      data-testid="refinement-char-warning"
                    >
                      {charLimitWarning}
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between mt-2">
                  <p
                    id="feedback-hint"
                    className="text-xs font-body text-muted"
                  >
                    Be specific about what you'd like to add, remove, or change.
                  </p>
                  <span
                    id="feedback-char-count"
                    className="text-xs font-body px-2 py-1 border border-dark-green/15 rounded-2xl"
                    style={{
                      backgroundColor: feedback.length > FEEDBACK_MAX_LENGTH * 0.9 ? 'rgba(var(--color-error-rgb, 220,53,69),0.15)' : '#F8F8F8',
                      color: feedback.length > FEEDBACK_MAX_LENGTH * 0.9 ? 'var(--color-error, #DC3545)' : '#4A4A4A',
                    }}
                    aria-live="polite"
                  >
                    {feedback.length}/{FEEDBACK_MAX_LENGTH}
                  </span>
                </div>
              </div>

              {/* Suggested prompts */}
              <div>
                <p className="text-xs font-display font-bold text-dark-green tracking-wider mb-2">
                  Suggestions:
                </p>
                <div className="flex flex-wrap gap-2">
                  {SUGGESTED_PROMPTS.map((prompt) => (
                    <button
                      key={prompt}
                      type="button"
                      onClick={() => handleSuggestedPrompt(prompt)}
                      className={`px-3 py-1 text-xs font-body border text-dark-green hover:-translate-y-0.5 transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-mid-green rounded-[6px] ${selectedSuggestion === prompt ? 'bg-light-green border-dark-green/30' : 'bg-white border-dark-green/15 hover:bg-light-green'}`}
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Submit/Cancel buttons */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    // Stop active recording before resetting form state
                    if (isListening) {
                      safeStopListening('cancel-form')
                    }
                    setShowFeedbackForm(false)
                    setFeedback('')
                    setSelectedSuggestion(null)
                  }}
                  disabled={isLoading}
                  className="flex-1 py-3 px-4 font-display font-bold text-sm tracking-wider border border-dark-green/15 bg-white text-dark-green shadow-denizen-sm rounded-2xl hover:-translate-y-1 active:shadow-none transition-all duration-150 focus:outline-none focus-visible:ring-4 focus-visible:ring-mid-green"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleRefine}
                  disabled={isLoading || !feedback.trim()}
                  className={`
                    flex-1 py-3 px-4 font-display font-bold text-sm tracking-wider
                    border border-dark-green/15 rounded-2xl
                    shadow-denizen-sm
                    transition-all duration-150
                    focus:outline-none focus-visible:ring-4 focus-visible:ring-mid-green focus-visible:ring-offset-2
                    ${
                      isLoading || !feedback.trim()
                        ? 'bg-muted text-white cursor-not-allowed'
                        : 'bg-dark-green text-white hover:-translate-y-1 active:shadow-none'
                    }
                  `}
                  aria-busy={isLoading || undefined}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg
                        className="w-4 h-4 animate-spin"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Generating...
                    </span>
                  ) : (
                    'Generate New Visions'
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Round indicator - soft dots */}
          <div className="mt-6 pt-4 border-t border-dark-green/15">
            <div className="flex items-center justify-between">
              <span className="text-xs font-display font-bold text-dark-green">
                Round {currentRound} of {maxRounds}
              </span>
              <div className="flex gap-2">
                {Array.from({ length: maxRounds }).map((_, i) => (
                  <div
                    key={i}
                    className={`
                      w-4 h-4 rounded-full border border-dark-green/15
                      ${i < currentRound ? 'bg-mid-green shadow-denizen-sm' : 'bg-white'}
                    `}
                    aria-hidden="true"
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Helper text when generating */}
      {isGenerating && (
        <p className="text-center text-muted text-sm mt-4 font-body">
          This usually takes 30-60 seconds
        </p>
      )}

      {/* Helper text when images available */}
      {!isGenerating && variations.length > 0 && (
        <p className="text-center text-muted text-sm mt-4 font-body">
          Click an image to select it, or tap the magnifying glass to view fullscreen
        </p>
      )}

      {/* Fullscreen preview lightbox */}
      <ImageLightbox
        imageUrl={previewImage?.image_url ?? ''}
        alt={previewImage ? 'Generated vision option fullscreen preview' : ''}
        isOpen={previewImage !== null}
        onClose={() => setPreviewImage(null)}
      />

      {/* Back confirmation dialog */}
      {showBackConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="back-confirm-title"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-dark-green/50"
            onClick={() => setShowBackConfirm(false)}
            aria-hidden="true"
          />

          {/* Dialog */}
          <div ref={dialogRef} className="relative bg-white border border-dark-green/15 shadow-denizen p-6 max-w-md mx-4 animate-slide-up rounded-2xl">
            {/* Round label */}
            <div className="pebble-label pebble-label-teal absolute -top-2 -right-3 text-xs">
              WAIT!
            </div>

            <h3
              id="back-confirm-title"
              className="font-display font-bold text-xl text-dark-green tracking-tight mb-3"
            >
              Go back a step?
            </h3>
            <p className="font-body text-sm text-muted mb-6 leading-relaxed">
              If you go back, these current images will drift into the future
              and won&apos;t be retrievable. You&apos;ll be able to select a different
              image from the previous round and explore new directions.
            </p>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowBackConfirm(false)}
                className="flex-1 py-3 px-4 font-display font-bold text-sm tracking-wider border border-dark-green/15 bg-white text-dark-green shadow-denizen-sm rounded-2xl hover:-translate-y-1 active:shadow-none transition-all duration-150 focus:outline-none focus-visible:ring-4 focus-visible:ring-mid-green"
              >
                Stay here
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowBackConfirm(false)
                  onGoBack?.()
                }}
                className="flex-1 py-3 px-4 font-display font-bold text-sm tracking-wider border border-dark-green/15 bg-dark-green text-white shadow-denizen-sm rounded-2xl hover:-translate-y-1 active:shadow-none transition-all duration-150 focus:outline-none focus-visible:ring-4 focus-visible:ring-mid-green"
              >
                Go back
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
})

export default RefinementFlow
